import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';

import type { StockItemResult } from '../_actions/searchLibraryImages';
import type { VideoStockItemResult } from '../_actions/searchLibraryVideos';
import { type ImageGenerationFormState } from './ImageGenerationFormState';
import { type VideoEditFormState } from './VideoEditFormState';
import { type VideoGenerationFormState } from './VideoGenerationFormState';
import type { FailureReason } from './failureReason';

export type AiTaskSubmitRouteSuccess = {
    success: true;
    taskId: number;
    status: string;
    balance: number;
    aiContentId: string;
    lastAiContentId: string;
};

export type AiTaskSubmitRouteError = {
    success: false;
    error: string;
    message?: string;
    failureReason?: FailureReason | null;
    balance?: number;
    aiContentId?: string;
    lastAiContentId?: string;
};

export type AiTaskSubmitRouteResponse = AiTaskSubmitRouteSuccess | AiTaskSubmitRouteError;

export type VoiceoverGenerationSubmitState = {
    status: AiToolkitSubmitStatus;
};

export type VideoGenerationPollResponse = {
    status: 'SUCCEEDED' | 'FAILED' | 'CANCELLED';
    videoUrl?: string | null;
    failureReason?: string | null;
};

export type VideoGenerationSubmitState = {
    pendingTaskId: number | null;
    videoUrl: string;
    sourceThumbnailUrl: string;
    aiContentId: string;
    lastAiContentId: string;
    aiContentType: AiContentType | null;
    aiModelName: string | null;
    status: AiToolkitSubmitStatus;
    restoredFromHistory: boolean;
    failureReason: FailureReason | null;
};

export type ImageGenerationPollResponse = {
    status: 'SUCCEEDED' | 'FAILED' | 'CANCELLED';
    imageUrl?: string | null;
    failureReason?: string | null;
};

export type ImageGenerationSubmitState = {
    status: AiToolkitSubmitStatus;
    pendingTaskId: number | null;
    imageUrl: string | null;
    aiContentId: string;
    lastAiContentId: string;
    aiContentType: AiContentType | null;
    aiModelName: string | null;
    restoredFromHistory: boolean;
    failureReason: FailureReason | null;
};

export type ImageGenerationSubmitAction =
    | {
          type: 'GENERATION_IN_PROGRESS';
          pendingTaskId?: number;
          aiContentId?: string;
          lastAiContentId?: string;
          aiContentType?: AiContentType | null;
          aiModelName?: string | null;
      }
    | {
          type: 'GENERATION_SUCCESS';
          imageUrl: string;
          aiContentId: string;
          lastAiContentId: string;
          aiContentType?: AiContentType | null;
          aiModelName?: string | null;
      }
    | { type: 'GENERATION_ERROR'; failureReason?: FailureReason | null }
    | { type: 'RESET' };

export type VoiceoverGenerationSubmitAction =
    | { type: 'GENERATION_IN_PROGRESS' }
    | { type: 'GENERATION_SUCCESS' }
    | { type: 'GENERATION_ERROR' }
    | { type: 'RESET' };

export type VideoGenerationSubmitAction =
    | {
          type: 'GENERATION_IN_PROGRESS';
          pendingTaskId?: number;
          aiContentId?: string;
          lastAiContentId?: string;
          aiContentType?: AiContentType | null;
          aiModelName?: string | null;
          sourceThumbnailUrl?: string;
      }
    | {
          type: 'GENERATION_SUCCESS';
          videoUrl: string;
          aiContentId: string;
          lastAiContentId: string;
          aiContentType?: AiContentType | null;
          aiModelName?: string | null;
      }
    | { type: 'GENERATION_ERROR'; failureReason?: FailureReason | null }
    | { type: 'RESET' };

export type AiToolkitActiveStatus = 'inProgress' | 'success' | 'error';
export type AiToolkitSubmitStatus = 'idle' | AiToolkitActiveStatus;

/**
 * Video edit is the only tool where the user picks a source video before submitting, so it adds a
 * `selection` status on top of the statuses shared by the other tools.
 */
export type VideoEditSubmitStatus = AiToolkitSubmitStatus | 'selection';

export type VideoEditPollResponse = {
    status: 'SUCCEEDED' | 'FAILED' | 'CANCELLED';
    videoUrl?: string | null;
    failureReason?: string | null;
};

export type LastCompletedVideoEdit = Pick<
    VideoEditSubmitState,
    | 'editedVideoUrl'
    | 'completedTaskId'
    | 'operation'
    | 'originalVideoUrl'
    | 'originalThumbnailUrl'
    | 'aiContentId'
    | 'lastAiContentId'
    | 'aiContentType'
    | 'aiModelName'
    | 'startTime'
    | 'duration'
    | 'contentId'
    | 'aspectRatio'
>;

export type VideoEditSubmitState = {
    pendingTaskId: number | null;
    /** The source video the user selected to edit. */
    originalVideoUrl: string;
    /** Image thumbnail of the source video, used for loading-state previews. */
    originalThumbnailUrl: string;
    /** The start time of the selection, in seconds. */
    startTime?: number;
    /** In seconds: the whole source video after a selection, then the edited clip once an edit is submitted. */
    duration: number;
    aspectRatio?: number;
    contentId: number | null;
    /** The generated result of the edit. */
    editedVideoUrl: string;
    /** Task that produced `editedVideoUrl`; an upscale submits it as `parentTaskId`. */
    completedTaskId: number | null;
    aiContentId: string;
    lastAiContentId: string;
    aiContentType: AiContentType | null;
    aiModelName: string | null;
    /** Which operation produced, or is producing, `editedVideoUrl`. */
    operation: AiToolkitOperation;
    status: VideoEditSubmitStatus;
    restoredFromHistory: boolean;
    failureReason: FailureReason | null;
    lastCompletedEdit: LastCompletedVideoEdit | null;
};

export type VideoEditSubmitAction =
    | {
          type: 'SELECTION';
          originalVideoUrl: string;
          originalThumbnailUrl: string;
          duration: number;
          contentId: number;
          aspectRatio?: number;
          startTime?: number;
      }
    | {
          type: 'GENERATION_IN_PROGRESS';
          operation?: AiToolkitOperation;
          pendingTaskId?: number;
          aiContentId?: string;
          lastAiContentId?: string;
          aiContentType?: AiContentType | null;
          aiModelName?: string | null;
          startTime?: number;
          duration?: number;
          contentId?: number | null;
      }
    | {
          type: 'GENERATION_SUCCESS';
          editedVideoUrl: string;
          aiContentId: string;
          lastAiContentId: string;
          aiContentType?: AiContentType | null;
          aiModelName?: string | null;
          operation?: AiToolkitOperation;
      }
    | { type: 'GENERATION_ERROR'; operation?: AiToolkitOperation; failureReason?: FailureReason | null }
    | { type: 'RESTORE_LAST_COMPLETED' }
    | { type: 'EDIT_SELECTION' }
    | { type: 'RESET' };

export type ConfirmationModalContent = {
    type: 'regenerate' | 'insufficientCredits' | 'entInsufficientCredits' | 'entFeatureAccess' | 'creditConfirmation';
    category: AiToolkitCategory;
    operation?: AiToolkitOperation;
};

export type ConfirmationModalState = (ConfirmationModalContent & { isOpen: boolean }) | null;

export type ConfirmationModalAction = { type: 'OPEN_MODAL'; modal: ConfirmationModalContent } | { type: 'CLOSE_MODAL' };

export const AI_TOOLKIT_CATEGORIES = {
    VIDEO_GENERATION: 'video-generation',
    VIDEO_EDIT: 'video-edit',
    IMAGE_GENERATION: 'image-generation',
    VOICEOVER: 'voiceover',
} as const;

export type AiToolkitCategory = (typeof AI_TOOLKIT_CATEGORIES)[keyof typeof AI_TOOLKIT_CATEGORIES];

export const AI_TOOLKIT_OPERATIONS = {
    GENERATE: 'generate',
    UPSCALE: 'upscale',
} as const;

export type AiToolkitOperation = (typeof AI_TOOLKIT_OPERATIONS)[keyof typeof AI_TOOLKIT_OPERATIONS];

export type ActiveTask = {
    category: AiToolkitCategory;
    operation: AiToolkitOperation;
    status: AiToolkitActiveStatus;
} | null;

export type AiToolkitFormStates = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]?: VideoGenerationFormState;
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]?: ImageGenerationFormState;
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]?: null;
    [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]?: VideoEditFormState;
};

export type AiToolkitSubmitStates = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: VideoGenerationSubmitState;
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: ImageGenerationSubmitState;
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]: VoiceoverGenerationSubmitState;
    [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: VideoEditSubmitState;
};

export const AI_USER_SEGMENTS = {
    UNSUBSCRIBED: 'unsubscribed',
    UPGRADABLE: 'upgradable',
    SUBSCRIBED: 'subscribed',
} as const;

export type AiUserSegment = (typeof AI_USER_SEGMENTS)[keyof typeof AI_USER_SEGMENTS];

export type AiToolkitContextValue = {
    credits: number;
    isEnterprise: boolean;
    subscriptionId: number | null;
    categorySegments: Record<AiToolkitCategory, AiUserSegment>;
    activeTask: ActiveTask;
    acknowledgeTask: (category: AiToolkitCategory, status: AiToolkitActiveStatus) => void;
    refreshCredits: () => Promise<number>;
    formStates: AiToolkitFormStates;
    submitStates: AiToolkitSubmitStates;
    updateFormState: <K extends keyof AiToolkitFormStates>(category: K, state: AiToolkitFormStates[K]) => void;
    dispatchVideoGenerationSubmit: (action: VideoGenerationSubmitAction) => void;
    dispatchVideoEditSubmit: (action: VideoEditSubmitAction) => void;
    dispatchImageGenerationSubmit: (action: ImageGenerationSubmitAction) => void;
    dispatchVoiceoverGenerationSubmit: (action: VoiceoverGenerationSubmitAction) => void;
    confirmationModal: ConfirmationModalState;
    dispatchConfirmationModal: (action: ConfirmationModalAction) => void;
    imageSearch: ImageSearchReturn;
    videoSearch: VideoSearchReturn;
    videoGenerationFileErrorMessage: string | null;
    setVideoGenerationFileErrorMessage: (message: string | null) => void;
    imageGenerationFileErrorMessage: string | null;
    setImageGenerationFileErrorMessage: (message: string | null) => void;
    videoGenerationSubmitError: string | null;
    setVideoGenerationSubmitError: (error: string | null) => void;
    imageGenerationSubmitError: string | null;
    setImageGenerationSubmitError: (error: string | null) => void;
    videoEditSubmitError: string | null;
    setVideoEditSubmitError: (error: string | null) => void;
};

export const SUPPORTED_IMAGE_SEARCH_MEDIA_TYPES = ['Photos', 'Illustrations', 'Snapshots'] as const;
export type SupportedImageSearchMediaType = (typeof SUPPORTED_IMAGE_SEARCH_MEDIA_TYPES)[number];

export const IMAGE_SEARCH_MEDIA_TYPE_DISPLAY_OPTIONS = ['Images', ...SUPPORTED_IMAGE_SEARCH_MEDIA_TYPES] as const;
export type ImageSearchMediaTypeOption = (typeof IMAGE_SEARCH_MEDIA_TYPE_DISPLAY_OPTIONS)[number];

export const SUPPORTED_IMAGE_SEARCH_FILE_TYPES = ['JPG', 'PNG'] as const;
export type SupportedImageSearchFileType = (typeof SUPPORTED_IMAGE_SEARCH_FILE_TYPES)[number];

export type ImageSearchReturn = {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    mediaType: ImageSearchMediaTypeOption;
    setMediaType: (type: ImageSearchMediaTypeOption) => void;
    results: StockItemResult[];
    isLoading: boolean;
    error: string | null;
    hasSearched: boolean;
    totalCount: number;
    hasMore: boolean;
    loadMore: () => Promise<void>;
    search: () => Promise<void>;
    reset: () => void;
    currentPage: number;
    totalPages: number;
    goToPage: (page: number) => Promise<void>;
    showSearch: boolean;
    setShowSearch: (show: boolean) => void;
};

// aleph2 edits real footage, so templates are not valid edit sources. Motion backgrounds are a
// planned addition — add them here once the model supports them.
export const SUPPORTED_VIDEO_SEARCH_MEDIA_TYPES = ['footage'] as const;
export type SupportedVideoSearchMediaType = (typeof SUPPORTED_VIDEO_SEARCH_MEDIA_TYPES)[number];

/**
 * Search-side floor mirroring the /api/video-edit `duration` minimum: clips shorter than this can
 * never produce a valid edit request, so they are filtered out of results.
 */
export const VIDEO_EDIT_MIN_SOURCE_DURATION = 2;

/**
 * Stockblocks `frame_rates[]` filter keys covering everything at or below 30fps.
 */
export const SUPPORTED_VIDEO_SEARCH_FRAME_RATES = ['fps24', 'fps25', 'fps30'] as const;

// Video edit has a single media type, so `mediaType` is read-only — there is no selector to mirror
// from ImageSearchReturn until more types are supported.
export type VideoSearchReturn = {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    mediaType: SupportedVideoSearchMediaType;
    results: VideoStockItemResult[];
    isLoading: boolean;
    error: string | null;
    hasSearched: boolean;
    totalCount: number;
    hasMore: boolean;
    loadMore: () => Promise<void>;
    search: () => Promise<void>;
    reset: () => void;
    currentPage: number;
    totalPages: number;
    goToPage: (page: number) => Promise<void>;
    showSearch: boolean;
    setShowSearch: (show: boolean) => void;
};
